/**
 * POM-6121 — the rules behind "Record a lot manually" on the bulk Review Lot & NDC picker.
 *
 * Pure, like every other module in this folder, and for the same two reasons:
 * `ChangeNdcLotDialog` is already close to the file-length limit, and these rules are the half
 * worth unit-testing. No React and no network. The one thing here that reads the clock is the BUD
 * floor, which is what "tomorrow or later" means — and it is the shared receiving/dispensing helper
 * rather than a second answer to the same question.
 *
 * Mirrors the single-item Fill Product panel shipped in POM-6083
 * (`PatientView/components/EligibilityActionItems/fillProductLotSelection`) rather than importing
 * it. Two things differ here, and they run through everything below:
 *
 *  1. A bulk row is ONE lot for the whole fill quantity, so there is no quantity field and no
 *     total to reconcile. The single-item panel holds several lots each with its own quantity.
 *  2. Nothing is fetched while the dialog is open. The zero-quantity lots ride with the batch
 *     review, so a typed lot number is matched against that same snapshot rather than looked up.
 */

// POM-6179 — the same lot-number, re-key and BUD rules the receiving and single-item fill screens
// apply, imported rather than restated. A second copy of "is this lot number well formed" is how
// two screens end up disagreeing about the value they both write to the same ledger.
import {
  LOT_NUMBER_MAX_LENGTH,
  UNSAFE_LOT_CHARACTERS,
  getDispensableBudHelperText,
  getDispensableBudMinIso,
  getLotConfirmHelperText,
  getLotHelperText,
  isLotBudExpired,
  isLotConfirmed,
  isValidLotNumber,
  lotConfirmFieldError,
  pickLotForBud,
} from 'modules/StockManagement/utils/stockLineValidation';

import { manualLotKey } from './bulkLotNdcStaging';
import { formatQty, isRecordableLot } from './bulkLotNdcStatus';

/** Case-insensitive, whitespace-tolerant comparison of two lot numbers. */
export const isSameLotNumber = (left, right) =>
  String(left ?? '')
    .trim()
    .toLowerCase() ===
  String(right ?? '')
    .trim()
    .toLowerCase();

/**
 * The NDC cards, in manual mode as well as out of it.
 *
 * Built from the union of the availability catalogue and the recordable NDC options, because
 * neither list alone can carry the card row:
 *
 *  - `availability.groups` is the dispensing catalogue. It holds the NDCs that have lots, so a
 *    product whose inventory master lists an NDC with no lots at all has no card for it — and that
 *    is precisely the NDC a hand-recorded claim belongs against.
 *  - `recordableLots.ndcOptions` is the closed inventory-master list. It carries no lots, so it
 *    cannot render the "N available" a stocked card shows.
 *
 * Merging them keeps ONE NDC control on the screen. A second dropdown inside the panel would make
 * two sources of truth for the same choice, which is the thing this dialog most has to avoid.
 *
 * Order: the catalogue groups first, in the order core sent them — that is how the shipped card
 * row already reads, and reordering it is not this ticket's to do — then the recordable-only NDCs
 * sorted case-insensitively, so the part of the list this ticket adds is not in whatever order the
 * API happened to return.
 */
export const buildNdcCards = ({ groups, ndcOptions, usableByNdc }) => {
  const cards = (groups ?? []).map((group) => ({
    ndc: group.ndc,
    manufacturer: group.manufacturer,
    isDefault: !!group.isDefault,
    usableLots: usableByNdc?.get(group.ndc) ?? [],
    // In the dispensing catalogue, so it is selectable outside manual mode too.
    isStockedNdc: true,
  }));

  const known = new Set(cards.map((card) => card.ndc));
  const recordableOnly = (ndcOptions ?? [])
    .filter((option) => !known.has(option.ndc))
    .map((option) => ({
      ndc: option.ndc,
      manufacturer: option.manufacturer,
      isDefault: !!option.isDefault,
      usableLots: [],
      // Never selectable outside manual mode: there is nothing here to dispense.
      isStockedNdc: false,
    }))
    .sort((left, right) => left.ndc.localeCompare(right.ndc, undefined, { sensitivity: 'base' }));

  return [...cards, ...recordableOnly];
};

/**
 * Is this NDC one a claim may be recorded against?
 *
 * The closed inventory-master list is the only answer. An NDC the operator can see on a card but
 * that inventory holds no master row for cannot be reconciled by anyone downstream, and core
 * refuses it outright — so it is refused here too, rather than letting the panel be filled in and
 * rejected on commit.
 */
export const isRecordableNdc = ({ ndc, ndcOptions }) =>
  !!ndc && (ndcOptions ?? []).some((option) => option.ndc === ndc);

/**
 * The zero-quantity lots on offer for one NDC.
 *
 * Scoped by NDC because a lot number is only unique within one: offering another NDC's lots
 * invites claiming a lot that does not exist there.
 *
 * ORDER IS DELIBERATELY NOT ALPHABETICAL. Core returns these most-recently-moved first, which is
 * the order the operator recognises the vial in their hand by — the lot they last dispensed from
 * is the one they are most likely holding. That order is carried through untouched, and this
 * comment is the exception the house sorting rule asks for.
 *
 * Lots typed for another row of the same batch are appended after them, so a lot number entered
 * once is offered to its siblings without being retyped — and retyping it is exactly how two
 * claims end up posted for one physical lot.
 */
export const offerableRecordableLots = ({ depletedLots, sessionLots, ndc }) => {
  if (!ndc) return [];

  const forNdc = (depletedLots ?? []).filter((lot) => lot.ndc === ndc && isRecordableLot({ lot }));
  const alreadyOffered = new Set(forNdc.map((lot) => manualLotKey(lot)));

  const fromSession = (sessionLots ?? [])
    .filter((lot) => lot.ndc === ndc && !alreadyOffered.has(manualLotKey(lot)))
    .map((lot) => ({ ...lot, isFromThisBatch: true }));

  return [...forNdc, ...fromSession];
};

/**
 * What a typed lot number resolves to against the snapshot.
 *
 * `matched` is the existing row to claim rather than create. Claiming the real row is what stops
 * one physical lot being split in two, with the negative on one row and the received history on
 * the other.
 *
 * `isAmbiguous` is the case core rejects as INVENTORY_LOT_BUD_AMBIGUOUS: the same lot number
 * exists more than once for this NDC under different expiry dates, so the number alone does not
 * say which vial is in hand. Caught here so the operator is told before they commit.
 */
export const resolveTypedLot = ({ lotNumber, lots }) => {
  const candidates = (lots ?? []).filter((lot) => isSameLotNumber(lot.lotNumber, lotNumber));

  if (candidates.length === 0) return { matched: null, isAmbiguous: false };
  if (candidates.length === 1) return { matched: candidates[0], isAmbiguous: false };

  const distinctBuds = new Set(candidates.map((lot) => String(lot.expirationDate ?? '')));

  return {
    // With one expiry date across the duplicates there is nothing ambiguous about it — the same
    // lot listed twice is still that lot.
    matched: distinctBuds.size === 1 ? candidates[0] : null,
    isAmbiguous: distinctBuds.size > 1,
  };
};

/**
 * A lot the operator typed that inventory still holds quantity for.
 *
 * Hand-recording is only for stock inventory has no quantity for, so this is a mistake with a
 * specific fix: pick it from the NDC card and lot list instead. Blocked here as well as
 * server-side, because a server-side refusal arrives after the whole panel has been filled in.
 */
export const stockedLotForTypedNumber = ({ lotNumber, ndc, availabilityGroups }) => {
  if (!lotNumber?.trim() || !ndc) return null;

  const group = (availabilityGroups ?? []).find((candidate) => candidate.ndc === ndc);

  return (
    (group?.lots ?? []).find(
      (lot) => isSameLotNumber(lot.lotNumber, lotNumber) && Number(lot.remainingQty) > 0
    ) ?? null
  );
};

/**
 * Why the panel cannot add this entry yet, or null when it can.
 *
 * One ordered expression rather than per-field errors: the panel shows a single alert, and the
 * first thing standing in the way is the only thing worth saying. The order is load-bearing — a
 * hard stop ("inventory holds no NDC for this product") has to come before a field problem, or the
 * operator is sent to fix a lot number on a product they can never record against.
 */
/**
 * The single-item panel's wording, restated here rather than imported: the two live in different
 * modules and this is the message a reviewer will compare word for word.
 */
const describeForeignOwners = (lots) =>
  (lots ?? [])
    .map((entry) => (entry.drugName ? `${entry.drugName} (NDC ${entry.ndc})` : `NDC ${entry.ndc}`))
    .join(', ');

const foreignLotMessage = (lots, lotNumber) =>
  `Lot ${lotNumber} already belongs to ${describeForeignOwners(lots)}. A lot number belongs to ` +
  `one product, so either the drug or the lot number is wrong — check the vial.`;

export const manualEntryError = ({
  hasInventoryMaster,
  ndcOptions,
  ndc,
  lotNumber,
  confirmLotNumber,
  pickedLot,
  isAmbiguousLotNumber,
  stockedLot,
  isBudTooEarly,
  foreignNdcLots,
  batchNdcsClaimingLot,
  lookupFailed,
  expiredLot,
}) => {
  if (!hasInventoryMaster || (ndcOptions ?? []).length === 0) {
    return 'Inventory holds no NDC for this product, so a lot cannot be recorded against it. Ask an inventory user to add the product first.';
  }

  if (!isRecordableNdc({ ndc, ndcOptions })) {
    return 'Select an NDC. A lot can only be recorded against an NDC inventory holds for this product.';
  }

  if (!lotNumber?.trim()) return 'Enter the lot number.';

  // POM-6179 — the same rule receiving applies. A lot number that reaches the ledger carrying a
  // space or a quote is one that will not match on the next scan.
  if (!isValidLotNumber(lotNumber)) {
    return (
      `Check the lot number — no spaces or ${UNSAFE_LOT_CHARACTERS}, ` +
      `up to ${LOT_NUMBER_MAX_LENGTH} characters.`
    );
  }

  if (stockedLot) {
    return `Lot ${stockedLot.lotNumber} has ${formatQty(
      stockedLot.remainingQty
    )} available. Select it from the lots above instead of recording it by hand.`;
  }

  /**
   * POM-6121 — the same cross-NDC check the single-item panel and receiving already apply, on the
   * same lookup and in the same words. A lot number identifies one product, so a number inventory
   * already holds under a different NDC means either the drug or the number is wrong.
   *
   * There is no server-side guard behind this one: core resolves a lot within the NDC's own
   * inventory item, so a number belonging elsewhere simply does not match and a fresh lot is
   * created under this NDC instead. That is why a failed lookup blocks rather than passes.
   */
  if (lookupFailed) {
    return 'Could not check this lot number against inventory. Try again in a moment.';
  }

  if ((foreignNdcLots ?? []).length > 0) {
    return foreignLotMessage(foreignNdcLots, String(lotNumber ?? '').trim());
  }

  // POM-6119 — the same rule, against the batch instead of inventory. Both lots being new makes
  // this collision invisible to the lookup above: it exists only inside this unsent batch. Worded
  // as "in this batch" because the remedy is different — the other row is on screen and editable,
  // where a lot inventory already owns is not.
  if ((batchNdcsClaimingLot ?? []).length > 0) {
    return (
      `Lot ${String(lotNumber ?? '').trim()} is already recorded for ${(batchNdcsClaimingLot ?? [])
        .map((other) => `NDC ${other}`)
        .join(', ')} in this batch. A lot number belongs to one product, so either the drug or ` +
      'the lot number is wrong — check the vial.'
    );
  }

  // POM-6121 — an existing lot whose own BUD has passed. Dispensing it is what is being recorded,
  // and an expired lot cannot be dispensed, so the claim is refused however the number arrived —
  // taken from the list, or typed against a row only the lookup can see.
  if (expiredLot) {
    return `Lot ${expiredLot.lotNumber ?? String(lotNumber ?? '').trim()} expired on ${String(
      expiredLot.expirationDate ?? ''
    ).slice(0, 10)}. An expired lot cannot be dispensed, so it cannot be recorded by hand either.`;
  }

  if (isAmbiguousLotNumber && !pickedLot) {
    return 'This lot number exists more than once for this NDC with different expiration dates. Pick the one you have from the list.';
  }

  /**
   * POM-6179 — the re-key.
   *
   * Only for a number the operator TYPED that names nothing on record. A lot taken from the list
   * — this product's own zero-quantity row, or one a sibling row already entered — carries
   * inventory's own value, so there is no typo for a second typing to catch, and demanding one
   * anyway is how a confirmation field becomes something operators learn to defeat.
   *
   * It matters more in bulk than on the single-item panel: a lot typed for one row is then offered
   * to every sibling row of the batch, so one typo propagates across the whole batch.
   *
   * Ordered AFTER the in-stock and ambiguous checks on purpose: both of those mean the number
   * itself is wrong, and asking for it to be typed a second time first would have the operator
   * re-key a value that was never going to be accepted.
   */
  if (!pickedLot && !isLotConfirmed(lotNumber, confirmLotNumber)) {
    return String(confirmLotNumber ?? '').trim()
      ? 'The two lot numbers do not match. Re-enter it exactly as typed above.'
      : 'Re-enter the lot number to confirm it.';
  }

  // The field carries a `min`, so this is only reachable by typing a date straight into it.
  if (isBudTooEarly) {
    return 'The expiration date must be tomorrow or later — an expired lot cannot be dispensed.';
  }

  return null;
};

/**
 * The staged lot allocation for one hand-recorded entry.
 *
 * Always the WHOLE fill quantity: a bulk row is one lot, so there is nothing to split and no
 * quantity for the operator to get wrong.
 *
 * `inventoryLotId` is null for a genuinely new lot number and set when an existing zero-quantity
 * row was claimed. `isManualEntry` is carried either way — it is what tells core the quantity
 * posts to the unverified bucket, and it cannot be inferred from a missing id once a real row can
 * be claimed by hand.
 */
export const buildManualLotSelection = ({
  lotNumber,
  ndc,
  expirationDate,
  quantity,
  inventoryLotId = null,
}) => ({
  inventoryLotId: inventoryLotId ?? null,
  lotNumber: String(lotNumber ?? '').trim(),
  ndc,
  // An empty string from a cleared date field must reach the API as null, not ''. Null means "no
  // BUD supplied"; '' fails date parsing server-side.
  expirationDate: expirationDate || null,
  quantity,
  isManualEntry: true,
});

/**
 * The hand-recorded lots already staged across this batch, for the other rows to reuse.
 *
 * Scoped to one product and facility, because that pair is what makes a lot number meaningful —
 * and deduped by the same key the staging module compares on, so two rows that typed the same
 * number offer it once.
 */
export const manualLotsStagedInBatch = ({
  items,
  changes,
  productId,
  fulfillingPharmacyId,
  excludeRequestLineItemId = null,
}) => {
  const byKey = new Map();

  (items ?? []).forEach((item) => {
    if (item.productId !== productId) return;
    if (item.fulfillingPharmacyId !== fulfillingPharmacyId) return;
    // The row being edited is not one of the OTHER rows. Leaving it in makes its own claim look
    // like an existing lot to itself, which locks the BUD it is trying to correct.
    if (item.requestLineItemId === excludeRequestLineItemId) return;

    (changes?.[item.requestLineItemId]?.lotAllocations ?? []).forEach((lot) => {
      if (!lot.isManualEntry) return;

      const key = manualLotKey(lot);
      if (byKey.has(key)) return;

      byKey.set(key, {
        inventoryLotId: lot.inventoryLotId ?? null,
        lotNumber: lot.lotNumber,
        ndc: lot.ndc,
        expirationDate: lot.expirationDate ?? null,
        // Zero by construction: a lot with quantity is never hand-recorded, and the panel's own
        // guard is what keeps that true.
        remainingQty: 0,
        unverifiedQty: null,
      });
    });
  });

  return [...byKey.values()];
};

/**
 * POM-6119 — which drugs this batch already records each lot number against.
 *
 * The mirror of manualLotsStagedInBatch: that one gathers a number's siblings so one vial can
 * cover several rows of the same product, this one asks whether the number has been given to a
 * DIFFERENT product. A manufacturer lot number identifies one product, so the second case is an
 * operator typing a number off the vial in their hand against the wrong drug.
 *
 * Deliberately local. The server refuses this too and that is the real guard — it also sees lots
 * inventory already holds, which no client can — but when both lots are new the collision exists
 * only inside this unsent batch, so nothing but the batch itself can see it before Commit. Left
 * to the server alone, the operator finds out after doing all the work.
 *
 * Keyed by lot number rather than asked one number at a time, because the value being typed lives
 * in the picker and the batch lives in the review dialog — a map crosses that line once.
 *
 * Lot numbers compare case-insensitively, as everywhere else here. NDCs compare as given: the
 * batch's own rows carry the strings the picker offered, so two spellings of one NDC cannot arise
 * the way they can across systems.
 */
export const ndcClaimsByLotNumber = ({ items, changes, excludeRequestLineItemId = null }) => {
  const byLot = new Map();

  (items ?? []).forEach((item) => {
    if (item.requestLineItemId === excludeRequestLineItemId) return;

    const change = changes?.[item.requestLineItemId];
    if (!change?.ndc) return;

    (change.lotAllocations ?? []).forEach((lot) => {
      const key = String(lot.lotNumber ?? '')
        .trim()
        .toLowerCase();
      if (!key) return;

      const claimed = byLot.get(key) ?? [];
      if (claimed.includes(change.ndc)) return;

      claimed.push(change.ndc);
      byLot.set(key, claimed);
    });
  });

  return byLot;
};

/**
 * Everything the picker needs to know about the REST of the batch, for one row.
 *
 * Composed here rather than in the dialog so both halves are derived from one row and one batch,
 * and cannot be handed a different `excludeRequestLineItemId` by accident.
 */
export const batchLotContextForRow = ({ items, changes, row }) => {
  if (!row) return { sessionManualLots: [], ndcClaims: new Map() };

  return {
    sessionManualLots: manualLotsStagedInBatch({
      items,
      changes,
      productId: row.productId,
      fulfillingPharmacyId: row.fulfillingPharmacyId,
      excludeRequestLineItemId: row.requestLineItemId,
    }),
    ndcClaims: ndcClaimsByLotNumber({
      items,
      changes,
      excludeRequestLineItemId: row.requestLineItemId,
    }),
  };
};

/**
 * Everything the manual panel needs, derived once.
 *
 * Called by `ChangeNdcLotDialog` and handed to `ChangeNdcLotManualEntry` as a single object, so
 * the panel that renders the state and the Apply button that gates on it cannot disagree: the
 * error that greys out "Add lot" is the same value, not a second evaluation of the same rules.
 */
export const deriveManualEntry = ({
  recordable,
  sessionLots,
  availabilityGroups,
  ndc,
  lotNumber,
  confirmLotNumber,
  bud,
  pickedLotId,
  fillQuantity,
  foreignNdcLots,
  batchNdcsClaimingLot,
  lookupFailed,
  sameNdcLotsOnFile,
  fulfillingPharmacyId,
  todayStr,
}) => {
  const ndcOptions = recordable?.ndcOptions ?? [];
  const lots = offerableRecordableLots({
    depletedLots: recordable?.depletedLots,
    sessionLots,
    ndc,
  });

  const { matched, isAmbiguous } = resolveTypedLot({ lotNumber, lots });
  /**
   * A typed number that names one existing row IS that row.
   *
   * Adopting its identity is what stops one physical lot being split in two — the negative on a
   * new row, the received history on the old one — and it means the operator does not have to
   * notice that the number they typed was already in the list below.
   */
  // Guarded on pickedLotId being set: a typed-new lot staged by a sibling row carries a null id,
  // so an unguarded find(id === null) matches THAT row's lot before the typed-number match below
  // is ever consulted — attaching another row's BUD to this one's claim.
  const pickedById = pickedLotId ? lots.find((lot) => lot.inventoryLotId === pickedLotId) : null;
  const pickedLot = pickedById ?? matched ?? null;

  const stockedLot = stockedLotForTypedNumber({ lotNumber, ndc, availabilityGroups });

  // An existing lot's BUD is part of its identity: correcting one is an inventory change with a
  // reason and an approver, and the API refuses it from a fill. A picked lot with NO BUD is the
  // opposite case — it is non-dispensable until someone supplies the date, and the operator
  // holding the vial can read it off the label, so that field stays open.
  const adoptedBud = pickedLot?.expirationDate
    ? String(pickedLot.expirationDate).slice(0, 10)
    : null;
  const isBudLocked = !!adoptedBud;
  const budValue = adoptedBud ?? bud ?? '';

  /**
   * POM-6179 — the BUD floor, and why it is conditional.
   *
   * This lot is being DISPENSED, not received, so a date already reached describes stock that
   * cannot go out — receiving tolerates a short-dated arrival, a fill cannot. Applied only while
   * the field is the operator's to type: a date adopted from a picked row is that lot's own, is not
   * theirs to fix, and re-validating it would block a claim on a lot inventory itself still holds.
   */
  const budTooEarly = isBudLocked ? '' : getDispensableBudHelperText(budValue);
  /**
   * POM-6121 — an adopted BUD used to be exempt from the floor, on the reasoning that an existing
   * lot's date is not the operator's to fix. That let an expired lot be claimed by hand. The date
   * still is not theirs to fix, but the claim records a dispense, and an expired lot cannot be
   * dispensed — so it is refused here and corrected in inventory instead.
   *
   * Judged by isLotBudExpired, NOT by the typing floor above: the floor is tomorrow-or-later, so a
   * lot expiring today would be called expired here while the stock list in this same dialog lists
   * it as usable, and the message would name a date that has not passed.
   */
  /**
   * The lot this number names on file, from BOTH sources — because neither is complete.
   *
   * `pickedLot` comes from the batch snapshot, which is this product at THIS facility. The lookup
   * is by lot number alone, so it also returns rows for this NDC at other sites, which the
   * snapshot cannot see. The single-item panel reads only the second; reading only the first is
   * what left this picker accepting a number that panel refuses.
   *
   * Narrowed to this fill's own site first, the way pickLotForBud narrows when it adopts a BUD:
   * one lot number legitimately sits at two sites, and another pharmacy's dead row must not refuse
   * a fill against stock that is in date here.
   */
  const lotOnFileElsewhere = pickLotForBud({
    entries: sameNdcLotsOnFile,
    receivedAtPharmacyId: fulfillingPharmacyId,
  });

  const expiredLot =
    [isBudLocked ? { ...pickedLot, expirationDate: adoptedBud } : null, lotOnFileElsewhere].find(
      (lot) => isLotBudExpired({ expirationDate: lot?.expirationDate, todayStr })
    ) ?? null;

  const error = manualEntryError({
    hasInventoryMaster: !!recordable?.hasInventoryMaster,
    ndcOptions,
    ndc,
    lotNumber,
    confirmLotNumber,
    pickedLot,
    isAmbiguousLotNumber: isAmbiguous,
    stockedLot,
    isBudTooEarly: !!budTooEarly,
    foreignNdcLots,
    batchNdcsClaimingLot,
    lookupFailed,
    expiredLot,
  });

  // The shared receiving copy when the value breaks the format rule — it names WHICH rule; the
  // ordinary guidance otherwise.
  const formatHelperText = getLotHelperText(false, lotNumber);

  let lotHelperText = 'Pick an existing lot or type a new one';
  if (formatHelperText) lotHelperText = formatHelperText;
  else if (pickedLot) lotHelperText = 'Existing lot — the quantity is claimed against this lot';
  else if (lotNumber?.trim()) lotHelperText = 'New lot — inventory has no record of it yet';

  let budHelperText = 'Optional — a barcode cannot carry it';
  if (budTooEarly) budHelperText = budTooEarly;
  else if (isBudLocked) budHelperText = "This lot's BUD is part of its identity";
  else if (pickedLot) budHelperText = 'This lot has no BUD — enter it from the label to record one';
  else if (lotNumber?.trim())
    budHelperText = 'Optional — leave blank if the vial does not show one';

  return {
    lots,
    pickedLot,
    stockedLot,
    isAmbiguousLotNumber: isAmbiguous,
    error,
    canAdd: !error,
    isBudLocked,
    budValue,
    lotHelperText,
    budHelperText,
    // A typed-new number is the only one re-keyed — see manualEntryError.
    needsLotConfirmation: !pickedLot,
    isLotNumberInvalid: !!lotNumber?.trim() && !isValidLotNumber(lotNumber),
    isConfirmMismatch: lotConfirmFieldError(false, lotNumber, confirmLotNumber),
    confirmHelperText:
      getLotConfirmHelperText(false, lotNumber, confirmLotNumber) ||
      'Type it again — paste is disabled',
    // Undefined while the date is adopted, so the picker is bounded only where the rule applies.
    budMin: isBudLocked ? undefined : getDispensableBudMinIso(),
    isBudTooEarly: !!budTooEarly,
    selection: error
      ? null
      : buildManualLotSelection({
          lotNumber,
          ndc,
          expirationDate: budValue,
          quantity: fillQuantity,
          inventoryLotId: pickedLot?.inventoryLotId ?? null,
        }),
  };
};
